"use client";

import { getAcademiesApi } from "@app/apis/Academies";
import { getRestaurantsApi } from "@app/apis/Dining";
import { getPerformanceSportsApi } from "@app/apis/PerformancePlus/sportsApis";
import { getAssetsActivitiesApi, getAssetsApi } from "@app/apis/Assets/assetsApis";

export type PermissionKey = {
  path: string;
  module: string;
};

export type ModuleId =
  | "home"
  | "userRoles"
  | "users"
  | "clubSettings"
  | "branchesManagement"
  | "languages"
  | "onboardingScreens"
  | "modulesControl"
  | "cancellationReasons"
  | "ratingReasons"
  | "crmMembers"
  | "crmInterests"
  | "walletsManagement"
  | "transactionsHistory"
  | "walletSettings"
  | "membersPasses"
  | "guestInvitations"
  | "ticketTypes"
  | "accessControlSettings"
  | "supportTickets"
  | "supportFaqs"
  | "supportTutorials"
  | "supportingPages"
  | "supportInfo"
  | "ticketsCategories"
  | "directingEmails"
  | "adsBanner"
  | "amenitiesManagement"
  | "notifications"
  | "academies"
  | "dining"
  | "diningDeliveryManagement"
  | "performancePlus"
  | "performanceTags"
  | "gym"
  | "gymMainInfo"
  | "gymSubscriptionPlans"
  | "gymBookings"
  | "gymCancellationRequests"
  | "activities"
  | "allAssets";

export type SearchEntity = {
  id: string;
  name: string;
};

export type SearchEntityDestination = {
  id: string;
  label: string;
  description: string;
  href: (branchId: string, entityId: string) => string;
  permission?: PermissionKey;
};

export type SearchModule = {
  id: ModuleId;
  label: string;
  description: string;
  modulePermission?: PermissionKey;
  entityPermission?: PermissionKey;
  landingHref: (branchId: string) => string;
  fetchEntities?: () => Promise<unknown>;
  mapEntities?: (response: unknown) => SearchEntity[];
  entityHref?: (branchId: string, entityId: string) => string;
  entityDestinations?: SearchEntityDestination[];
};

function unwrapList(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.results)) return res.results;
  return [];
}

function navigationModule(
  id: ModuleId,
  label: string,
  description: string,
  href: string,
  modulePermission?: PermissionKey,
  query?: Record<string, string>,
): SearchModule {
  const qs = query ? `?${new URLSearchParams(query).toString()}` : "";
  return {
    id,
    label,
    description,
    modulePermission,
    landingHref: (branchId) => `/${branchId}${href}${qs}`,
  };
}

function entityDestination(
  id: string,
  label: string,
  description: string,
  path: (entityId: string) => string,
): SearchEntityDestination {
  return {
    id,
    label,
    description,
    href: (branchId, entityId) => `/${branchId}${path(entityId)}`,
  };
}

function academyDestination(
  id: string,
  label: string,
  description: string,
  page: string,
  query?: Record<string, string>,
): SearchEntityDestination {
  const suffix = query
    ? `?${new URLSearchParams(query).toString()}`
    : "";
  return entityDestination(
    id,
    label,
    description,
    (academyId) => `/academies/${academyId}/${page}${suffix}`,
  );
}

function diningDestination(
  id: string,
  label: string,
  description: string,
  page: string,
  query?: Record<string, string>,
): SearchEntityDestination {
  const suffix = query
    ? `?${new URLSearchParams(query).toString()}`
    : "";
  return entityDestination(
    id,
    label,
    description,
    (restaurantId) => `/dining/${restaurantId}/${page}${suffix}`,
  );
}

function performanceDestination(
  id: string,
  label: string,
  description: string,
  page: string,
  query?: Record<string, string>,
): SearchEntityDestination {
  const suffix = query
    ? `?${new URLSearchParams(query).toString()}`
    : "";
  return entityDestination(
    id,
    label,
    description,
    (sportId) => `/performance-plus/${sportId}/${page}${suffix}`,
  );
}

function activityDestination(
  id: string,
  label: string,
  description: string,
  page: string,
): SearchEntityDestination {
  return entityDestination(
    id,
    label,
    description,
    (activityId) => `/assets-rental/${activityId}/${page}`,
  );
}

const ACADEMY_ENTITY_DESTINATIONS: SearchEntityDestination[] = [
  academyDestination("main-info", "Main Info", "Open academy main information", "main-info"),
  academyDestination(
    "structure-programs",
    "Structure: Programs",
    "Open academy structure — programs",
    "structure",
    { tab: "programs" },
  ),
  academyDestination(
    "structure-seasons",
    "Structure: Seasons",
    "Open academy structure — seasons",
    "structure",
    { tab: "seasons" },
  ),
  academyDestination(
    "structure-groups",
    "Structure: Groups",
    "Open academy structure — groups",
    "structure",
    { tab: "groups" },
  ),
  academyDestination(
    "structure-training-sessions",
    "Structure: Training sessions",
    "Open academy structure — training sessions",
    "structure",
    { tab: "training-sessions" },
  ),
  academyDestination(
    "subscription-plans",
    "Subscription Plans",
    "Open academy subscription plans",
    "subscription-plans",
  ),
  academyDestination(
    "assets-management",
    "Assets Management",
    "Open academy assets management",
    "assets-management",
  ),
  academyDestination(
    "trainers-management",
    "Trainers Management",
    "Open academy trainers management",
    "trainers-management",
  ),
  academyDestination(
    "assessments-slots",
    "Assessments: Slots",
    "Open academy assessments — slots",
    "assessments",
    { tab: "slots" },
  ),
  academyDestination(
    "assessments-bookings",
    "Assessments: Bookings",
    "Open academy assessments — bookings",
    "assessments",
    { tab: "bookings" },
  ),
  academyDestination(
    "assessments-program-unlock",
    "Assessments: Program unlock",
    "Open academy assessments — program unlock",
    "assessments",
    { tab: "program-unlock" },
  ),
  academyDestination(
    "reports-management",
    "Reports Management",
    "Open academy reports management",
    "reports-management",
  ),
  academyDestination("bookings", "Bookings", "Open academy bookings", "bookings"),
  academyDestination("calendar", "Calendar", "Open academy calendar", "calendar"),
  academyDestination("waiting-list", "Waiting List", "Open academy waiting list", "waiting-list"),
  academyDestination(
    "requests-edit",
    "Requests: Edit requests",
    "Open academy edit requests",
    "requests",
    { tab: "edit-requests" },
  ),
  academyDestination(
    "requests-cancel",
    "Requests: Cancel requests",
    "Open academy cancel requests",
    "requests",
    { tab: "cancel-requests" },
  ),
  academyDestination("cancellations", "Cancellation", "Open academy cancellations", "cancellations"),
];

const DINING_ENTITY_DESTINATIONS: SearchEntityDestination[] = [
  diningDestination("main-info", "Main Info", "Open restaurant main information", "main-info"),
  diningDestination(
    "products-categories",
    "Products: Categories",
    "Open restaurant product categories",
    "products",
    { tab: "categories" },
  ),
  diningDestination(
    "products-products",
    "Products: Products",
    "Open restaurant products list",
    "products",
    { tab: "products" },
  ),
  diningDestination(
    "products-variants",
    "Products: Variants",
    "Open restaurant product variants",
    "products",
    { tab: "variants" },
  ),
  diningDestination(
    "products-variants-options",
    "Products: Variants options",
    "Open restaurant variant options",
    "products",
    { tab: "variants-options" },
  ),
  diningDestination("orders", "Orders", "Open restaurant orders", "orders"),
];

const PERFORMANCE_ENTITY_DESTINATIONS: SearchEntityDestination[] = [
  performanceDestination("main-info", "Main Info", "Open sport main information", "main-info"),
  performanceDestination(
    "service-management-services",
    "Service Management: Services",
    "Open sport services",
    "service-management",
    { tab: "services" },
  ),
  performanceDestination(
    "service-management-session-templates",
    "Service Management: Session templates",
    "Open sport session templates",
    "service-management",
    { tab: "session-templates" },
  ),
  performanceDestination(
    "service-management-sport-sessions",
    "Service Management: Sport sessions",
    "Open sport sessions",
    "service-management",
    { tab: "sport-sessions" },
  ),
  performanceDestination(
    "instructors-management",
    "Instructors Management",
    "Open sport instructors management",
    "instructors-management",
  ),
  performanceDestination(
    "packages-management-packages",
    "Packages Management: Packages",
    "Open sport performance packages",
    "packages-management",
    { tab: "packages" },
  ),
  performanceDestination(
    "packages-management-bookings",
    "Packages Management: Packages bookings",
    "Open sport package bookings",
    "packages-management",
    { tab: "packages-bookings" },
  ),
  performanceDestination(
    "packages-cancellation-requests",
    "Packages Cancellation Requests",
    "Open sport packages cancellation requests",
    "packages-cancellation-requests",
  ),
  performanceDestination(
    "assets-management",
    "Assets Management",
    "Open sport assets management",
    "assets-management",
  ),
  performanceDestination("bookings", "Bookings", "Open sport bookings", "bookings"),
  performanceDestination("calendar", "Calendar", "Open sport calendar", "calendar"),
];

const ACTIVITY_ENTITY_DESTINATIONS: SearchEntityDestination[] = [
  activityDestination("main-info", "Main Info", "Open activity main information", "main-info"),
  activityDestination("assets", "Assets", "Open activity assets", "assets"),
  activityDestination("bookings", "Bookings", "Open activity bookings", "bookings"),
  activityDestination("calendar", "Calendar", "Open activity calendar", "calendar"),
];

export const SEARCH_MODULES: SearchModule[] = [
  navigationModule(
    "home",
    "Home",
    "Open the branch home page",
    "",
  ),
  navigationModule(
    "userRoles",
    "Roles",
    "Users Management roles and permissions",
    "/users-management/roles",
  ),
  navigationModule(
    "users",
    "Users",
    "Users Management admin users",
    "/users-management/users",
  ),
  navigationModule(
    "clubSettings",
    "Club Settings",
    "Configurations for club settings",
    "/configurations/club-settings",
    { path: "configurations", module: "clubSettings" },
  ),
  navigationModule(
    "branchesManagement",
    "Branches Management",
    "Configurations for branches management",
    "/configurations/branches-management",
    { path: "configurations", module: "branchesManagement" },
  ),
  navigationModule(
    "languages",
    "Languages",
    "Configurations for app languages",
    "/configurations/languages",
    { path: "configurations", module: "languages" },
  ),
  navigationModule(
    "onboardingScreens",
    "Onboarding Screens",
    "Configurations for onboarding screens",
    "/configurations/onboarding-screens",
    { path: "configurations", module: "onboardingScreens" },
  ),
  navigationModule(
    "modulesControl",
    "Modules Control",
    "Configurations for modules control",
    "/configurations/modules-control",
    { path: "configurations", module: "modulesControl" },
  ),
  navigationModule(
    "cancellationReasons",
    "Cancellation Reasons",
    "Configurations for cancellation reasons",
    "/configurations/cancellation-reasons",
    { path: "configurations", module: "cancellationReasons" },
  ),
  navigationModule(
    "ratingReasons",
    "Rating Reasons",
    "Configurations for rating reasons",
    "/configurations/rating-reasons",
    { path: "configurations", module: "ratingReasons" },
  ),
  navigationModule(
    "crmMembers",
    "Members",
    "CRM members",
    "/crm/members",
    { path: "crm", module: "members" },
  ),
  navigationModule(
    "crmInterests",
    "Interests",
    "CRM interests",
    "/crm/interests",
    { path: "crm", module: "interests" },
  ),
  navigationModule(
    "walletsManagement",
    "Wallets Management",
    "Wallet accounts and balances",
    "/wallet/wallets-management",
    { path: "wallet", module: "walletsManagement" },
  ),
  navigationModule(
    "transactionsHistory",
    "Transactions History",
    "Wallet transactions history",
    "/wallet/transactions-history",
    { path: "wallet", module: "transactionsHistory" },
  ),
  navigationModule(
    "walletSettings",
    "Wallet Settings",
    "Wallet configuration settings",
    "/wallet/wallet-settings",
    { path: "wallet", module: "walletSettings" },
  ),
  navigationModule(
    "membersPasses",
    "Members Passes",
    "Access Control members passes",
    "/access-control/members-passes",
    { path: "accessControl", module: "membersPasses" },
  ),
  navigationModule(
    "guestInvitations",
    "Guest Invitations",
    "Access Control guest invitations",
    "/access-control/guests-invitations",
    { path: "accessControl", module: "guestInvitations" },
  ),
  navigationModule(
    "ticketTypes",
    "Tickets Types",
    "Access Control ticket types",
    "/access-control/ticket-types",
    { path: "accessControl", module: "ticketTypes" },
  ),
  navigationModule(
    "accessControlSettings",
    "Access Control Settings",
    "Access Control settings",
    "/access-control/settings",
    { path: "accessControl", module: "settings" },
  ),
  navigationModule(
    "supportTickets",
    "Tickets",
    "Customer Support tickets",
    "/customer-support/tickets",
    { path: "customerSupport", module: "tickets" },
  ),
  navigationModule(
    "supportFaqs",
    "FAQs",
    "Customer Support FAQs",
    "/customer-support/faqs",
    { path: "customerSupport", module: "faqs" },
  ),
  navigationModule(
    "supportTutorials",
    "Tutorials",
    "Customer Support tutorials",
    "/customer-support/tutorials",
    { path: "customerSupport", module: "tutorials" },
  ),
  navigationModule(
    "supportingPages",
    "Supporting Pages",
    "Customer Support supporting pages",
    "/customer-support/supporting-pages",
    { path: "customerSupport", module: "supportingPages" },
  ),
  navigationModule(
    "supportInfo",
    "Support Info",
    "Customer Support settings and support info",
    "/customer-support/settings/support-info",
    { path: "customerSupport", module: "supportInfo" },
  ),
  navigationModule(
    "ticketsCategories",
    "Tickets Categories",
    "Customer Support ticket categories",
    "/customer-support/settings/tickets-categories",
    { path: "customerSupport", module: "ticketsCategories" },
  ),
  navigationModule(
    "directingEmails",
    "Directing Emails",
    "Customer Support directing emails",
    "/customer-support/settings/directing-emails",
    { path: "customerSupport", module: "directingEmails" },
  ),
  navigationModule(
    "adsBanner",
    "Ads Banner",
    "Marketing ads banner",
    "/marketing/ads-banner",
    { path: "marketing", module: "adsBanner" },
  ),
  navigationModule(
    "amenitiesManagement",
    "Amenities Management",
    "Marketing amenities management",
    "/marketing/amenities-management",
    { path: "marketing", module: "amenitiesManagement" },
  ),
  navigationModule(
    "notifications",
    "Notifications",
    "Marketing notifications",
    "/marketing/notifications",
    { path: "marketing", module: "notifications" },
  ),
  {
    id: "academies",
    label: "Academies",
    description: "Manage academies and open a specific academy",
    modulePermission: { path: "academies", module: "academiesManagement" },
    entityPermission: { path: "academies", module: "details" },
    landingHref: (branchId) => `/${branchId}/academies/academies-management`,
    fetchEntities: () => getAcademiesApi(),
    mapEntities: (res) =>
      unwrapList(res).map((a: any) => ({
        id: String(a?.id ?? ""),
        name: String(a?.name ?? ""),
      })),
    entityHref: (branchId, academyId) =>
      `/${branchId}/academies/${academyId}/main-info`,
    entityDestinations: ACADEMY_ENTITY_DESTINATIONS,
  },
  {
    id: "dining",
    label: "Dining",
    description: "Restaurants management and restaurant pages",
    modulePermission: { path: "dining", module: "details" },
    entityPermission: { path: "dining", module: "details" },
    landingHref: (branchId) => `/${branchId}/dining/restaurants`,
    fetchEntities: () => getRestaurantsApi(),
    mapEntities: (res) =>
      unwrapList(res).map((r: any) => ({
        id: String(r?.id ?? ""),
        name: String(r?.name ?? ""),
      })),
    entityHref: (branchId, restaurantId) =>
      `/${branchId}/dining/${restaurantId}/main-info`,
    entityDestinations: DINING_ENTITY_DESTINATIONS,
  },
  navigationModule(
    "diningDeliveryManagement",
    "Delivery Management",
    "Dining delivery zones and subzones",
    "/dining/delivery-management",
    { path: "dining", module: "delivery-management" },
  ),
  {
    id: "performancePlus",
    label: "Performance Plus",
    description: "Sports management and sport pages",
    modulePermission: { path: "performance_plus", module: "sportsManagement" },
    entityPermission: { path: "performance_plus", module: "details" },
    landingHref: (branchId) => `/${branchId}/performance-plus/sports-management`,
    fetchEntities: () => getPerformanceSportsApi(),
    mapEntities: (res) =>
      unwrapList(res).map((s: any) => ({
        id: String(s?.id ?? ""),
        name: String(s?.name ?? ""),
      })),
    entityHref: (branchId, sportId) =>
      `/${branchId}/performance-plus/${sportId}/main-info`,
    entityDestinations: PERFORMANCE_ENTITY_DESTINATIONS,
  },
  navigationModule(
    "performanceTags",
    "Tags Management",
    "Performance Plus tags management",
    "/performance-plus/tags-management",
    { path: "performance_plus", module: "tagsManagement" },
  ),
  {
    id: "gym",
    label: "Gym",
    description: "Gym dashboard",
    // Gym pages currently don’t use usePermission() in this codebase, so we leave permissions undefined.
    landingHref: (branchId) => `/${branchId}/gym/dashboard`,
  },
  navigationModule(
    "gymMainInfo",
    "Gym Main Info",
    "Gym main information",
    "/gym/main-info",
  ),
  navigationModule(
    "gymSubscriptionPlans",
    "Gym Subscription Plans",
    "Gym subscription plans",
    "/gym/subscription-plans",
  ),
  navigationModule(
    "gymBookings",
    "Gym Subscription Plan Bookings",
    "Gym bookings",
    "/gym/bookings",
  ),
  navigationModule(
    "gymCancellationRequests",
    "Gym Cancellation Requests",
    "Gym subscription plan cancellation requests",
    "/gym/bookings",
    undefined,
    { tab: "requests" },
  ),
  {
    id: "activities",
    label: "Activities",
    description: "Assets Rental activities management and activity pages",
    modulePermission: { path: "assets_rental", module: "details" },
    entityPermission: { path: "assets_rental", module: "details" },
    landingHref: (branchId) => `/${branchId}/assets-rental/assets-management`,
    fetchEntities: () => getAssetsActivitiesApi(),
    mapEntities: (res) =>
      unwrapList(res).map((a: any) => ({
        id: String(a?.id ?? ""),
        name: String(a?.name ?? ""),
      })),
    entityHref: (branchId, activityId) =>
      `/${branchId}/assets-rental/${activityId}/main-info`,
    entityDestinations: ACTIVITY_ENTITY_DESTINATIONS,
  },
  {
    id: "allAssets",
    label: "All Assets",
    description: "View and edit assets",
    modulePermission: { path: "assets_rental", module: "all_assets" },
    entityPermission: { path: "assets_rental", module: "all_assets" },
    landingHref: (branchId) => `/${branchId}/assets-rental/all-assets`,
    fetchEntities: () => getAssetsApi(),
    mapEntities: (res) =>
      unwrapList(res).map((a: any) => ({
        id: String(a?.id ?? ""),
        name: String(a?.name ?? ""),
      })),
    // There is no dedicated asset details route in `src/app/[branch_id]/assets-rental/...` today.
    // Selecting an asset will navigate to the module list page (future: prefilter/highlight via query params).
    entityHref: (branchId) => `/${branchId}/assets-rental/all-assets`,
  },
];
